import type { CmtsStoredSession } from "../auth/authStore.js";
import type { CmtsConfidentialityClearance, CmtsExternalPartyType } from "./cmtsSessionTypes.js";
import { CMTS_ROLE_CODES } from "../auth/cmtsRoleCodes.js";

const ROLE_PERSONA: Record<string, string> = {
  [CMTS_ROLE_CODES.SYSTEM_ADMIN]: "System Administrator",
  [CMTS_ROLE_CODES.TENANT_ADMIN]: "Tenant Administrator",
  [CMTS_ROLE_CODES.MANAGEMENT]: "Management / CXO",
  [CMTS_ROLE_CODES.QUALITY_HEAD]: "Quality Head",
  [CMTS_ROLE_CODES.QUALITY_MANAGER]: "Quality Manager",
  [CMTS_ROLE_CODES.DEPARTMENT_MANAGER]: "Department Manager",
  [CMTS_ROLE_CODES.COMPLAINT_CREATOR]: "Complaint Creator",
  [CMTS_ROLE_CODES.INVESTIGATOR]: "Investigator",
  [CMTS_ROLE_CODES.RCA_LEAD]: "RCA Lead",
  [CMTS_ROLE_CODES.CAPA_OWNER]: "CAPA Owner",
  [CMTS_ROLE_CODES.VERIFICATION_OWNER]: "Verifier",
  [CMTS_ROLE_CODES.CUSTOMER_USER]: "Customer Contact",
  [CMTS_ROLE_CODES.SUPPLIER_USER]: "Supplier Contact",
  [CMTS_ROLE_CODES.AUDITOR]: "Auditor",
  [CMTS_ROLE_CODES.REPORT_MANAGER]: "Report Manager",
  [CMTS_ROLE_CODES.EVIDENCE_ADMIN]: "Evidence Administrator",
  [CMTS_ROLE_CODES.SECURITY_VIEWER]: "Security Viewer",
};

const ROLE_DEPARTMENTS: Record<string, string[]> = {
  [CMTS_ROLE_CODES.SYSTEM_ADMIN]: ["IT / System Admin"],
  [CMTS_ROLE_CODES.TENANT_ADMIN]: ["IT / System Admin"],
  [CMTS_ROLE_CODES.MANAGEMENT]: ["Management"],
  [CMTS_ROLE_CODES.QUALITY_HEAD]: ["Quality", "Supplier Quality"],
  [CMTS_ROLE_CODES.QUALITY_MANAGER]: ["Quality"],
  [CMTS_ROLE_CODES.DEPARTMENT_MANAGER]: ["Production"],
  [CMTS_ROLE_CODES.COMPLAINT_CREATOR]: ["Sales / Customer Support"],
  [CMTS_ROLE_CODES.INVESTIGATOR]: ["Quality"],
  [CMTS_ROLE_CODES.RCA_LEAD]: ["Quality"],
  [CMTS_ROLE_CODES.CAPA_OWNER]: ["Production"],
  [CMTS_ROLE_CODES.VERIFICATION_OWNER]: ["Quality"],
  [CMTS_ROLE_CODES.AUDITOR]: ["Audit"],
  [CMTS_ROLE_CODES.REPORT_MANAGER]: ["Management"],
  [CMTS_ROLE_CODES.EVIDENCE_ADMIN]: ["Quality"],
  [CMTS_ROLE_CODES.SECURITY_VIEWER]: ["IT / System Admin"],
};

export function resolveCmtsPersona(roles: readonly string[] = [], fallback = "CMTS User"): string {
  return roles.map((role) => ROLE_PERSONA[role]).find(Boolean) ?? fallback;
}

export function resolveCmtsDepartmentScope(session: Pick<CmtsStoredSession, "roles" | "departmentIds">): string[] {
  const explicit = Array.from(new Set((session.departmentIds ?? []).map(String).filter(Boolean)));
  if (explicit.length) return explicit;
  const roles = session.roles ?? [];
  return Array.from(new Set(roles.flatMap((role) => ROLE_DEPARTMENTS[String(role)] ?? [])));
}

export function resolveCmtsExternalPartyType(roles: readonly string[] = []): CmtsExternalPartyType | undefined {
  if (roles.includes(CMTS_ROLE_CODES.CUSTOMER_USER)) return "CUSTOMER";
  if (roles.includes(CMTS_ROLE_CODES.SUPPLIER_USER)) return "SUPPLIER";
  return undefined;
}

export function resolveCmtsCustomerScope(session: Pick<CmtsStoredSession, "roles" | "customerIds">): string[] {
  const explicit = Array.from(new Set((session.customerIds ?? []).map(String).filter(Boolean)));
  if (explicit.length) return explicit;
  return (session.roles ?? []).includes(CMTS_ROLE_CODES.CUSTOMER_USER) ? ["customer-acme", "ACME Auto Components", "ACME Mobility Pvt Ltd"] : [];
}

export function resolveCmtsSupplierScope(session: Pick<CmtsStoredSession, "roles" | "supplierIds">): string[] {
  const explicit = Array.from(new Set((session.supplierIds ?? []).map(String).filter(Boolean)));
  if (explicit.length) return explicit;
  return (session.roles ?? []).includes(CMTS_ROLE_CODES.SUPPLIER_USER) ? ["supplier-precision", "Precision Supplier Pvt Ltd"] : [];
}

export function resolveCmtsClearance(roles: readonly string[] = []): CmtsConfidentialityClearance {
  if (roles.includes(CMTS_ROLE_CODES.SYSTEM_ADMIN) || roles.includes(CMTS_ROLE_CODES.TENANT_ADMIN) || roles.includes(CMTS_ROLE_CODES.SECURITY_VIEWER)) return "RESTRICTED";
  if (roles.includes(CMTS_ROLE_CODES.AUDITOR) || roles.includes(CMTS_ROLE_CODES.MANAGEMENT) || roles.includes(CMTS_ROLE_CODES.QUALITY_HEAD)) return "CONFIDENTIAL";
  if (roles.includes(CMTS_ROLE_CODES.CUSTOMER_USER) || roles.includes(CMTS_ROLE_CODES.SUPPLIER_USER)) return "PUBLIC";
  return "INTERNAL";
}
